import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { workflowSetConditionalTextField } from '~/actions/workflow';
import ConditionRuleInput from '~/components/Workflow/shared/ConditionRuleInput';
import Field from '~/components/Workflow/shared/Field';
import FieldPlaceholder from '~/components/Workflow/shared/FieldPlaceholder';
import { useIntl } from 'react-intl';
import { getConditionalTextFields } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import PageLayout from '~/ui/components/PageLayout/PageLayout';
import Text from '~/ui/components/Text/Text';
import TextInput from '~/ui/components/TextInput/TextInput';
import { useModalSimple } from '~/ui/hooks/modal';
import { testInvalidConditionRule } from '~/utils/workflow';

const { SidebarSection, SidebarSectionItem } = PageLayout;

function LabelContentPair({ children, label, rightHeaderContent }) {
  return (
    <FlexLayout flexDirection="column" space={3}>
      <FlexLayout alignItems="center" justifyContent="space-between">
        <Text color="gray-700" variant="s-dense-bold">
          {label}
        </Text>
        {rightHeaderContent}
      </FlexLayout>
      {children}
    </FlexLayout>
  );
}

function ConditionalTextFieldSection({
  // connected
  conditions,
  disabled,
  fields,
  workflowSetConditionalTextField,
}) {
  const intl = useIntl();
  const [selectedField, setSelectedField] = useState();
  const [name, setName] = useState('');
  const [conditionRule, setConditionRule] = useState();

  useEffect(() => {
    if (selectedField) {
      setName(selectedField.name);
      setConditionRule(selectedField.conditionRule);
    } else {
      setName('');
      setConditionRule();
    }
  }, [selectedField]);

  function confirm() {
    if (selectedField) {
      workflowSetConditionalTextField({
        ...selectedField,
        name,
        conditionRule,
      });
    }
  }

  function cancel() {
    setSelectedField();
  }

  function handleEdit(field) {
    setSelectedField(field);
    showEditModal();
  }

  const [editModal, showEditModal] = useModalSimple({
    confirm,
    disableAutoHide: true,
    text: intl.formatMessage({
      id: 'ConditionalTextFieldSection.saveButton',
      defaultMessage: 'Save',
      description: 'Save button text',
    }),
    title: intl.formatMessage({
      id: 'ConditionalTextFieldSection.editModalTitle',
      defaultMessage: 'Edit Conditional Text',
      description: 'Title for edit conditional text modal',
    }),
    content: selectedField && (
      <FlexLayout flexDirection="column" space={6}>
        <LabelContentPair
          label={intl.formatMessage({
            id: 'ConditionalTextFieldSection.nameLabel',
            defaultMessage: 'Name',
            description: 'Label for name field',
          })}
          rightHeaderContent={
            <FieldPlaceholder
              value={selectedField.conditionalText.placeholder}
            />
          }
        >
          <TextInput value={name} onChange={setName} width="fullWidth" />
        </LabelContentPair>
        <LabelContentPair
          label={intl.formatMessage({
            id: 'ConditionalTextFieldSection.visibilityLabel',
            defaultMessage: 'Visibility',
            description: 'Label for visibility field',
          })}
        >
          <ConditionRuleInput
            conditionRule={conditionRule}
            onChange={setConditionRule}
          />
        </LabelContentPair>
        {selectedField.conditionalText.texts.map((text, i) => (
          <LabelContentPair
            key={i}
            label={intl.formatMessage({
              id: 'ConditionalTextFieldSection.contentLabel',
              defaultMessage: 'Content',
              description: 'Label for content field',
            })}
          >
            <Text color="gray-700" variant="s-spaced">
              {text}
            </Text>
          </LabelContentPair>
        ))}
      </FlexLayout>
    ),
    width: 'l',
    onHide: cancel,
  });

  if (fields.length === 0) {
    return null;
  }

  return (
    <SidebarSection
      title={intl.formatMessage({
        id: 'ConditionalTextFieldSection.sectionTitle',
        defaultMessage: 'Conditional Text',
        description: 'Title for conditional text section',
      })}
    >
      {fields.map((field) => {
        let warningTooltip;
        if (!field.conditionRule) {
          warningTooltip = intl.formatMessage({
            id: 'ConditionalTextFieldSection.conditionRequiredWarning',
            defaultMessage: 'A condition must be assigned',
            description: 'Warning when no condition is assigned',
          });
        } else if (testInvalidConditionRule(field.conditionRule, conditions)) {
          warningTooltip = intl.formatMessage({
            id: 'ConditionalTextFieldSection.invalidConditionWarning',
            defaultMessage: 'Invalid condition assigned',
            description: 'Warning when an invalid condition is assigned',
          });
        }
        return (
          <SidebarSectionItem
            key={field.id}
            onClick={() => (disabled ? null : handleEdit(field))}
            role="listitem"
          >
            <Field
              assignedLabel={
                conditions[field?.conditionRule?.id]?.name ||
                intl.formatMessage({
                  id: 'ConditionalTextFieldSection.unassignedLabel',
                  defaultMessage: 'Unassigned',
                  description: 'Label for unassigned condition',
                })
              }
              disabled={disabled}
              field={field}
              isAssigned={!!field.conditionRule}
              warningTooltip={warningTooltip}
            />
          </SidebarSectionItem>
        );
      })}
      {editModal}
    </SidebarSection>
  );
}

function mapStateToProps({ builder, workflow }) {
  return {
    disabled: !builder.shouldEnableAllComponents,
    fields: getConditionalTextFields(workflow),
    conditions: workflow.conditions,
  };
}

export default connect(mapStateToProps, { workflowSetConditionalTextField })(
  ConditionalTextFieldSection,
);
